import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import {
  geometryBindingUnit,
  isRoomBinding,
  validateProjectFeatureInput,
  type FeatureAction,
  type FeatureDefinition,
  type FeatureGeometryBinding,
  type ProjectFeature,
  type ProjectFeatureInput,
  type QuantityUnit,
} from "../../domain/feature";
import type { MeasurementSource } from "../../domain/measurement";
import { ValidationError } from "./projectRepository";

interface DefinitionRow {
  id: string;
  display_name: string;
  default_action: string;
  sort_order: number;
  is_system: number;
  archived_at: string | null;
}

interface FeatureRow {
  id: string;
  project_id: string;
  design_id: string | null;
  origin_id: string | null;
  room_id: string | null;
  floor_id: string | null;
  feature_definition_id: string | null;
  name: string;
  action: string;
  quantity_x100: number | null;
  quantity_unit: string | null;
  quantity_source: string | null;
  quantity_geometry_binding: string | null;
  quantity_geometry_x100: number | null;
  finish_key: string | null;
  notes: string;
  sort_order: number;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

function mapDefinition(row: DefinitionRow): FeatureDefinition {
  return {
    id: row.id,
    displayName: row.display_name,
    defaultAction: row.default_action as FeatureAction,
    sortOrder: Number(row.sort_order),
    isSystem: Number(row.is_system) === 1,
    archivedAt: row.archived_at,
  };
}

function mapFeature(row: FeatureRow): ProjectFeature {
  return {
    id: row.id,
    projectId: row.project_id,
    designId: row.design_id ?? null,
    originId: row.origin_id ?? null,
    roomId: row.room_id,
    floorId: row.floor_id,
    featureDefinitionId: row.feature_definition_id,
    name: row.name,
    action: row.action as FeatureAction,
    quantityX100: row.quantity_x100 === null ? null : Number(row.quantity_x100),
    quantityUnit: row.quantity_unit as QuantityUnit | null,
    quantitySource: row.quantity_source as MeasurementSource | null,
    quantityGeometryBinding: (row.quantity_geometry_binding as FeatureGeometryBinding | null) ?? null,
    quantityGeometryX100: row.quantity_geometry_x100 === null || row.quantity_geometry_x100 === undefined ? null : Number(row.quantity_geometry_x100),
    finishKey: row.finish_key ?? null,
    notes: row.notes,
    sortOrder: Number(row.sort_order),
    archivedAt: row.archived_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

const SELECT_FEATURES = `SELECT id, project_id, design_id, origin_id, room_id, floor_id, feature_definition_id,
                                name, action, quantity_x100, quantity_unit, quantity_source,
                                quantity_geometry_binding, quantity_geometry_x100, finish_key, notes, sort_order, archived_at, created_at, updated_at
                           FROM project_features`;

export class FeatureRepository {
  constructor(private readonly db: DbExecutor) {}

  // ----- Feature definitions (reusable library) -----

  async listDefinitions(): Promise<FeatureDefinition[]> {
    const rows = await this.db.select<DefinitionRow>(
      `SELECT id, display_name, default_action, sort_order, is_system, archived_at
         FROM feature_definitions ORDER BY sort_order, display_name`,
    );
    return rows.map(mapDefinition);
  }

  async createDefinition(
    displayName: string,
    defaultAction: FeatureAction,
  ): Promise<FeatureDefinition> {
    const name = displayName.trim();
    if (name === "") {
      throw new ValidationError(["Feature name is required."]);
    }
    const existing = await this.listDefinitions();
    const maxSort = existing.reduce((m, d) => Math.max(m, d.sortOrder), 0);
    const definition: FeatureDefinition = {
      id: generateId(),
      displayName: name,
      defaultAction,
      sortOrder: maxSort + 10,
      isSystem: false,
      archivedAt: null,
    };
    await this.db.execute(
      `INSERT INTO feature_definitions (id, display_name, default_action, sort_order, is_system)
       VALUES (?, ?, ?, ?, 0)`,
      [definition.id, definition.displayName, definition.defaultAction, definition.sortOrder],
    );
    return definition;
  }

  /** Renaming a definition never rewrites historical project features
   *  (instances own their copied names). */
  async renameDefinition(id: string, displayName: string): Promise<void> {
    const name = displayName.trim();
    if (name === "") {
      throw new ValidationError(["Feature name is required."]);
    }
    await this.db.execute("UPDATE feature_definitions SET display_name = ? WHERE id = ?", [
      name,
      id,
    ]);
  }

  async archiveDefinition(id: string): Promise<void> {
    await this.db.execute(
      "UPDATE feature_definitions SET archived_at = ? WHERE id = ? AND archived_at IS NULL",
      [new Date().toISOString(), id],
    );
  }

  async unarchiveDefinition(id: string): Promise<void> {
    await this.db.execute("UPDATE feature_definitions SET archived_at = NULL WHERE id = ?", [id]);
  }

  /** Suggested definitions for a room type, in suggestion order.
   *  Suggestions only — any active definition may be attached anywhere. */
  async listSuggestionsForRoomType(roomTypeId: string): Promise<FeatureDefinition[]> {
    const rows = await this.db.select<DefinitionRow>(
      `SELECT d.id, d.display_name, d.default_action, d.sort_order, d.is_system, d.archived_at
         FROM room_type_feature_suggestions s
         JOIN feature_definitions d ON d.id = s.feature_definition_id
        WHERE s.room_type_id = ? AND d.archived_at IS NULL
        ORDER BY s.sort_order`,
      [roomTypeId],
    );
    return rows.map(mapDefinition);
  }

  // ----- Project feature instances -----

  async listByProject(projectId: string): Promise<ProjectFeature[]> {
    const rows = await this.db.select<FeatureRow>(
      `${SELECT_FEATURES} WHERE project_id = ? ORDER BY sort_order, created_at`,
      [projectId],
    );
    return rows.map(mapFeature);
  }

  async getFeature(id: string): Promise<ProjectFeature | null> {
    const rows = await this.db.select<FeatureRow>(`${SELECT_FEATURES} WHERE id = ?`, [id]);
    return rows.length > 0 ? mapFeature(rows[0]) : null;
  }

  /** Room/floor must belong to the same project as the feature. */
  private async assertScopeValid(projectId: string, input: ProjectFeatureInput): Promise<void> {
    if (input.roomId !== null) {
      const rows = await this.db.select<{ project_id: string }>(
        `SELECT f.project_id FROM rooms r JOIN floors f ON f.id = r.floor_id WHERE r.id = ?`,
        [input.roomId],
      );
      if (rows.length === 0) {
        throw new ValidationError(["The selected room no longer exists."]);
      }
      if (rows[0].project_id !== projectId) {
        throw new ValidationError(["A feature can only attach to a room in the same project."]);
      }
    }
    if (input.floorId !== null) {
      const rows = await this.db.select<{ project_id: string }>(
        "SELECT project_id FROM floors WHERE id = ?",
        [input.floorId],
      );
      if (rows.length === 0) {
        throw new ValidationError(["The selected floor no longer exists."]);
      }
      if (rows[0].project_id !== projectId) {
        throw new ValidationError(["A feature can only attach to a floor in the same project."]);
      }
    }
    if (input.featureDefinitionId !== null) {
      const rows = await this.db.select<{ id: string }>(
        "SELECT id FROM feature_definitions WHERE id = ?",
        [input.featureDefinitionId],
      );
      if (rows.length === 0) {
        throw new ValidationError(["The selected feature definition no longer exists."]);
      }
    }
  }

  /** Features visible for one evaluated design: common (design_id NULL) + that design's. */
  async listForDesign(projectId: string, designId: string | null): Promise<ProjectFeature[]> {
    const rows = designId
      ? await this.db.select<FeatureRow>(
          `${SELECT_FEATURES} WHERE project_id = ? AND (design_id IS NULL OR design_id = ?) ORDER BY sort_order, created_at`,
          [projectId, designId],
        )
      : await this.db.select<FeatureRow>(`${SELECT_FEATURES} WHERE project_id = ? AND design_id IS NULL ORDER BY sort_order, created_at`, [projectId]);
    return rows.map(mapFeature);
  }

  /** Move a feature between Common Scope (null) and a specific design. */
  async setDesign(id: string, designId: string | null): Promise<void> {
    await this.db.execute("UPDATE project_features SET design_id = ?, updated_at = ? WHERE id = ?", [designId, new Date().toISOString(), id]);
  }

  async create(projectId: string, input: ProjectFeatureInput): Promise<ProjectFeature> {
    const problems = validateProjectFeatureInput(input);
    if (problems.length > 0) {
      throw new ValidationError(problems);
    }
    await this.assertScopeValid(projectId, input);
    const existing = await this.listByProject(projectId);
    const maxSort = existing.reduce((m, f) => Math.max(m, f.sortOrder), 0);
    const now = new Date().toISOString();
    const feature: ProjectFeature = {
      id: generateId(),
      projectId,
      designId: input.designId ?? null,
      originId: null,
      // The finish system stamps this itself when it owns the feature (§82).
      finishKey: null,
      roomId: input.roomId,
      floorId: input.floorId,
      featureDefinitionId: input.featureDefinitionId,
      name: input.name.trim(),
      action: input.action,
      quantityX100: input.quantityX100,
      quantityUnit: input.quantityX100 === null ? null : input.quantityUnit,
      quantitySource: input.quantityX100 === null ? null : "manual",
      quantityGeometryBinding: null,
      quantityGeometryX100: null,
      notes: input.notes,
      sortOrder: maxSort + 10,
      archivedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    await this.db.execute(
      `INSERT INTO project_features
         (id, project_id, design_id, origin_id, room_id, floor_id, feature_definition_id, name, action,
          quantity_x100, quantity_unit, quantity_source, notes, sort_order,
          created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        feature.id,
        feature.projectId,
        feature.designId,
        feature.originId,
        feature.roomId,
        feature.floorId,
        feature.featureDefinitionId,
        feature.name,
        feature.action,
        feature.quantityX100,
        feature.quantityUnit,
        feature.quantitySource,
        feature.notes,
        feature.sortOrder,
        feature.createdAt,
        feature.updatedAt,
      ],
    );
    return feature;
  }

  /** Update name/action/quantity/notes (scope attachment is immutable in Phase 3). */
  async update(id: string, input: ProjectFeatureInput): Promise<void> {
    const problems = validateProjectFeatureInput(input);
    if (problems.length > 0) {
      throw new ValidationError(problems);
    }
    await this.db.execute(
      `UPDATE project_features SET
         name = ?, action = ?, quantity_x100 = ?, quantity_unit = ?,
         quantity_source = CASE WHEN ? IS NULL THEN NULL WHEN quantity_geometry_binding IS NOT NULL THEN 'override' ELSE 'manual' END,
         notes = ?, updated_at = ?
       WHERE id = ?`,
      [
        input.name.trim(),
        input.action,
        input.quantityX100,
        input.quantityX100 === null ? null : input.quantityUnit,
        input.quantityX100,
        input.notes,
        new Date().toISOString(),
        id,
      ],
    );
  }

  /** Archiving a feature never modifies financial records (their links
   *  and active states are independent financial truth). */
  /**
   * Archive the feature ONLY. Its estimate lines are deliberately left
   * alone (Phase 3 contract: archiving scope never touches a financial
   * record), which is why callers that mean "remove this scope from the
   * estimate" use `archiveWithLines` below.
   */
  async archive(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute(
      "UPDATE project_features SET archived_at = ?, updated_at = ? WHERE id = ? AND archived_at IS NULL",
      [now, now, id],
    );
  }

  /**
   * REMOVE SCOPE: archive the feature and the estimate lines it owns.
   *
   * What the user removes must leave the working estimate — a feature whose
   * cost lingered would inflate the estimate and hide a reduction from
   * baseline comparison (spec §27). Lines are archived, never deleted, so
   * any actual cost linked to them stays readable (spec §11), which is the
   * same rule Phase 10's package removal follows.
   */
  async archiveWithLines(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.transaction(async () => {
      await this.db.execute(
        "UPDATE proposed_cost_lines SET archived_at = ?, updated_at = ? WHERE feature_id = ? AND archived_at IS NULL",
        [now, now, id],
      );
      await this.db.execute(
        "UPDATE project_features SET archived_at = ?, updated_at = ? WHERE id = ? AND archived_at IS NULL",
        [now, now, id],
      );
    });
  }

  // ----- Phase 7: geometry quantity binding -----

  async getById(id: string): Promise<ProjectFeature | null> {
    const rows = await this.db.select<FeatureRow>(`${SELECT_FEATURES} WHERE id = ?`, [id]);
    return rows.length === 0 ? null : mapFeature(rows[0]);
  }

  /** Room-attached, non-archived features bound to geometry for the given rooms. */
  async listGeometryBoundForRooms(roomIds: string[]): Promise<ProjectFeature[]> {
    if (roomIds.length === 0) return [];
    const placeholders = roomIds.map(() => "?").join(",");
    const rows = await this.db.select<FeatureRow>(
      `${SELECT_FEATURES} WHERE archived_at IS NULL AND quantity_geometry_binding IS NOT NULL AND room_id IN (${placeholders}) ORDER BY sort_order`,
      roomIds,
    );
    return rows.map(mapFeature);
  }

  /**
   * Bind (or unbind) the feature quantity to its room's geometry. Binding
   * makes geometry the effective source ('geometry'); the manual value is
   * preserved as an inactive override. Unbinding returns to the manual value.
   */
  async setGeometryBinding(id: string, binding: FeatureGeometryBinding | null, geometryX100: number | null): Promise<void> {
    const feature = await this.getById(id);
    if (!feature) throw new ValidationError(["Feature not found."]);
    // Room-geometry bindings need a room; OBJECT bindings do not (a cabinet
    // run or island can sit outside any detected room and still drive scope).
    const needsRoom = isRoomBinding(binding);
    if (needsRoom && feature.roomId === null) throw new ValidationError(["Only room features can use room geometry."]);
    const now = new Date().toISOString();
    if (binding === null) {
      await this.db.execute(
        `UPDATE project_features SET quantity_geometry_binding = NULL, quantity_geometry_x100 = NULL,
           quantity_source = CASE WHEN quantity_x100 IS NULL THEN NULL ELSE 'manual' END, updated_at = ? WHERE id = ?`,
        [now, id],
      );
      return;
    }
    await this.db.execute(
      `UPDATE project_features SET quantity_geometry_binding = ?, quantity_geometry_x100 = ?,
         quantity_source = CASE WHEN quantity_source = 'override' THEN 'override' ELSE 'geometry' END, updated_at = ? WHERE id = ?`,
      [binding, geometryX100, now, id],
    );
  }

  /**
   * Spec §90-27: this quantity came from a package or a renovation standard,
   * not from the user. It stays a default (and says so) until somebody
   * changes it, at which point the ordinary update writes 'manual'.
   */
  async markQuantityFromPackage(id: string): Promise<void> {
    await this.db.execute(
      `UPDATE project_features SET quantity_source = 'package', updated_at = ?
        WHERE id = ? AND quantity_x100 IS NOT NULL AND quantity_geometry_binding IS NULL`,
      [new Date().toISOString(), id],
    );
  }

  /**
   * Spec §89: a quantity somebody TYPED stays theirs. When work entered
   * without a drawing is later drawn, the drawing's measurement is kept
   * beside the typed figure (never silently replacing it); "Use the
   * measurement" is the explicit way back.
   */
  async keepTypedQuantityAsOverride(id: string): Promise<void> {
    await this.db.execute(
      `UPDATE project_features SET quantity_source = 'override', updated_at = ?
        WHERE id = ? AND quantity_x100 IS NOT NULL AND quantity_geometry_binding IS NULL`,
      [new Date().toISOString(), id],
    );
  }

  /** Set / clear a manual override on a geometry-bound feature (calculated value preserved). */
  async setQuantityOverride(id: string, valueX100: number | null): Promise<void> {
    const feature = await this.getById(id);
    if (!feature) throw new ValidationError(["Feature not found."]);
    if (!feature.quantityGeometryBinding) throw new ValidationError(["This feature is not bound to geometry."]);
    if (valueX100 !== null && (!Number.isSafeInteger(valueX100) || valueX100 < 0)) {
      throw new ValidationError(["Quantity must be a valid non-negative number."]);
    }
    // The override is stated in the binding's own unit. This used to write
    // 'lf' for everything but floor area, so overriding a wall area, a finish
    // area or a count made the sqft/each assembly refuse the quantity (§89).
    const unit = geometryBindingUnit(feature.quantityGeometryBinding);
    await this.db.execute(
      `UPDATE project_features SET quantity_x100 = ?, quantity_unit = ?, quantity_source = ?, updated_at = ? WHERE id = ?`,
      [valueX100, valueX100 === null ? null : unit, valueX100 === null ? "geometry" : "override", new Date().toISOString(), id],
    );
  }

  /** Coordinator: refresh the cached geometry-derived quantity. */
  async setGeometryQuantity(id: string, geometryX100: number | null): Promise<void> {
    await this.db.execute("UPDATE project_features SET quantity_geometry_x100 = ?, updated_at = ? WHERE id = ?", [
      geometryX100,
      new Date().toISOString(),
      id,
    ]);
  }

  async unarchive(id: string): Promise<void> {
    await this.db.execute(
      "UPDATE project_features SET archived_at = NULL, updated_at = ? WHERE id = ?",
      [new Date().toISOString(), id],
    );
  }
}
